import * as React from 'react';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ProductTile } from '@src/stories/ecommerce/Product/ProductTile';
import { CollectionSidebar } from '@src/stories/ecommerce/Collection/CollectionSidebar';
import { CollectionToolbar } from '@src/stories/ecommerce/Collection/CollectionToolbar';
import useMobileBreakpoint from '@arctheme-hooks/useMobileBreakpoint';
import { LoadingSpinner } from '@arctheme-components/elements/LoadingSpinner/LoadingSpinner';
import { getSearchProvider } from '@src/providers/Search';
import { useSearch } from '@src/contexts/SearchProvider';
import { useSearchSettings } from '@src/contexts/SearchSettingsContext';
import { SearchProvider, useSearchContext } from '@src/contexts/SearchContext';
import shopify from 'frontend/js/shopify';
import ProductListPagination from '@src/stories/ecommerce/Product/ProductListPagination';
import './Search.scss';
import { SearchInput } from './SearchHeaderBlocks/SearchInput';
import { useResponsiveBreakpoints } from '@src/hooks/useMediaQuery';
import { ObviyoProductRecommendations } from '@src/stories/blocks/ObviyoRecommendations/ObviyoProductRecommendations';
import Preloader from '@src/stories/client/Preloader/Preloader';

export const SearchResults = ({ settings }) => {
  const { isMobile } = useResponsiveBreakpoints();
  const { searchSettings, updateSearchSettings } = useSearchSettings();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<any>(null);

  // Use a ref to store the sidebar toggle function to avoid re-renders
  const sidebarToggleRef = useRef<(() => void) | null>(null);

  const setSidebarToggle = useCallback((toggleFn: () => void) => {
    sidebarToggleRef.current = toggleFn;
  }, []);

  const toggleSidebar = useCallback(() => {
    if (sidebarToggleRef.current) {
      sidebarToggleRef.current();
    }
  }, []);

  // Extract settings
  const {
    columns_mobile,
    columns_tablet,
    columns_desktop,
    columns_ultrawide,
    column_gap_mobile,
    column_gap_tablet,
    column_gap_desktop,
    column_gap_ultrawide,
    row_gap_mobile,
    row_gap_tablet,
    row_gap_desktop,
    row_gap_ultrawide,
    id,
    heading_type,
    products_per_page,
    toolbar_layout,
    product_count_position,
    sidebar_position,
    show_filter_icon,
    show_sort_label,
    filter_sort_direction,
    disable_filtering,
    filters: metaobject_filters,
    search_size
  } = settings.section;

  const filterButtonSettings = settings.filter_button_settings;
  const sortDropdownSettings = settings.sort_dropdown_settings;
  const productTileSettings = settings.product_tile_settings;

  // Perform search function similar to SearchHeader
  const performSearch = useCallback(
    (searchQuery: string) => {
      setIsSearching(true);
      setSearchError('');

      // Clear any existing search timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }

      // Fire search request to the search provider
      const searchRequestEvent = new CustomEvent('searchrequest', {
        detail: {
          type: 'search',
          query: searchQuery,
          filters_object: {},
          productsPerPage: products_per_page || 24,
          page: 1,
          paginationType: 'pages',
          sortOption: settings.section?.default_sort || 'RELEVANCE',
          collectionSize: search_size || 0
        }
      });

      window.dispatchEvent(searchRequestEvent);

      // Set timeout for search requests
      const timeout = setTimeout(() => {
        setIsSearching(false);
        setSearchError('Search request timed out. Please try again.');
      }, 10000);

      setSearchTimeout(timeout);
    },
    [searchTimeout, products_per_page, settings.section?.default_sort, search_size]
  );

  // Listen for search results to clear timeout
  useEffect(() => {
    const handleSearchResults = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { type } = customEvent.detail;

      // Only handle results if this is a search results page search
      if (type === 'search') {
        // Clear any existing timeout
        if (searchTimeout) {
          clearTimeout(searchTimeout);
          setSearchTimeout(null);
        }

        setIsSearching(false);
        setSearchError('');
      }
    };

    // Listen for search errors
    const handleSearchError = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { message } = customEvent.detail;

      // Clear any existing timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
        setSearchTimeout(null);
      }

      setIsSearching(false);
      setSearchError(message || 'An error occurred while searching');
    };

    // Add event listeners for search results and errors
    window.addEventListener('searchrequest-products-complete', handleSearchResults);
    window.addEventListener('searchrequest-error', handleSearchError);

    // Cleanup
    return () => {
      window.removeEventListener('searchrequest-products-complete', handleSearchResults);
      window.removeEventListener('searchrequest-error', handleSearchError);

      // Clear any pending timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
    };
  }, [searchTimeout, isSearching]);

  // Only dispatch initial search event after SearchProvider is mounted
  useEffect(() => {
    // Wait for SearchProvider to be mounted and context to be ready
    // Get the search query from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get('q') || '';
    setQuery(searchQuery);

    // Explicitly type filtersFromUrl as any[] to avoid TS error
    const filtersFromUrl: any[] = [];
    for (const [key, value] of urlParams.entries()) {
      const filter = shopify.generateProductFilterFromParam(key, value);
      if (filter) (filtersFromUrl as any[]).push(filter);
    }

    // Update global search settings if not yet initialized
    if (!searchSettings.initialized) {
      updateSearchSettings({
        search_system: settings.search_system || 'shopify',
        settings: settings
      });
    }

    // Dispatch initial search event only after provider/context is ready
    // Use a microtask to ensure SearchProvider is mounted
    Promise.resolve().then(() => {
      window.dispatchEvent(
        new CustomEvent('searchrequest', {
          detail: {
            type: 'search',
            query: searchQuery,
            filters_object: {},
            productsPerPage: settings.section?.products_per_page,
            page: 1,
            paginationType: 'pages',
            sortOption: settings.section?.default_sort || 'RELEVANCE',
            collectionSize: search_size || 0,
          },
        })
      );
      if (searchQuery) {
        setLoading(true);
        window.dispatchEvent(
          new CustomEvent('show-spinner', { detail: { id: 'spinner-search' } })
        );
      } else {
        setLoading(false);
      }
    });

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        // Close any open sidebar by dispatching a general close event
        // The sidebar component will handle whether it's actually open or not
        const sidebarElement = document.querySelector('[data-sidebar-container]');
        if (sidebarElement && sidebarToggleRef.current) {
          // Only toggle if the sidebar appears to be visible (has transform: translateX(0))
          const sidebar = sidebarElement.querySelector('[data-sidebar]');
          if (sidebar && sidebar.classList.contains('translate-x-0')) {
            sidebarToggleRef.current();
          }
        }
      }
    };
    window.addEventListener('keydown', handleEscapeKey);
    return () => {
      window.removeEventListener('keydown', handleEscapeKey);
    };
  }, [searchSettings.initialized]);

  const providerName = searchSettings.initialized ? searchSettings.search_system : settings.search_system || 'shopify';
  const SearchProviderComponent = getSearchProvider(providerName);

  const SearchProviderProxy = useCallback((props) => <SearchProviderComponent {...props} />, [SearchProviderComponent]);

  const columns = useMemo(
    () => ({
      mobile: columns_mobile,
      tablet: columns_tablet,
      desktop: columns_desktop,
      ultrawide: columns_ultrawide,
      gapMobile: column_gap_mobile,
      gapTablet: column_gap_tablet,
      gapDesktop: column_gap_desktop,
      gapUltrawide: column_gap_ultrawide,
      rowGapMobile: row_gap_mobile,
      rowGapTablet: row_gap_tablet,
      rowGapDesktop: row_gap_desktop,
      rowGapUltrawide: row_gap_ultrawide
    }),
    [
      columns_mobile,
      columns_tablet,
      columns_desktop,
      column_gap_mobile,
      column_gap_tablet,
      column_gap_desktop,
      row_gap_mobile,
      row_gap_tablet,
      row_gap_desktop
    ]
  );

  const defaultSettings = useMemo(() => ({ productsPerPage: products_per_page || 24 }), [products_per_page]);
  const providerSettings = useMemo(
    () => (searchSettings.initialized ? searchSettings.settings : settings),
    [searchSettings.initialized, searchSettings.settings, settings]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const searchQuery = urlParams.get('q') || '';

    const handleProductClick = () => {
      sessionStorage.setItem('searchScrollPos', window.scrollY.toString());
      sessionStorage.setItem('searchQuery', searchQuery);
      sessionStorage.setItem('searchScrollTimestamp', Date.now().toString());
    };

    const handlePageScroll = () => {
      const savedScrollPos = sessionStorage.getItem('searchScrollPos');
      const scrollTimestamp = sessionStorage.getItem('searchScrollTimestamp');
      // Only restore if we have a scroll position and it's recent (within 10 minutes)
      const isRecentNavigation = scrollTimestamp && (Date.now() - parseInt(scrollTimestamp)) < 600000;
      if (savedScrollPos && isRecentNavigation) {
        // Restore scroll position immediately since products are already loaded
        setTimeout(() => {
          window.scrollTo({ top: parseInt(savedScrollPos, 10), behavior: 'smooth' });
          sessionStorage.removeItem('searchScrollPos');
          sessionStorage.removeItem('searchQuery');
          sessionStorage.removeItem('searchScrollTimestamp');
        }, 100);
      }
    };

    // Listen for products loaded events to trigger scroll restoration
    window.addEventListener('search-products-loaded', handlePageScroll);

    // Add click handlers to product links
    const addProductClickHandlers = () => {
      const productLinks = document.querySelectorAll('.search-tile a');
      productLinks.forEach((link) => {
        link.removeEventListener('click', handleProductClick);
        link.removeEventListener('keydown', handleProductClick);
        link.addEventListener('click', handleProductClick);
        link.addEventListener('keydown', handleProductClick);
      });
    };

    // Set up a mutation observer to watch for product tile changes
    const observer = new MutationObserver(() => {
      addProductClickHandlers();
    });
    // Start observing the product grid
    const productGrid = document.querySelector('.search-results-container');
    if (productGrid) {
      observer.observe(productGrid, { childList: true, subtree: true });
    }
    // Initial handler setup
    addProductClickHandlers();

    return () => {
      window.removeEventListener('search-products-loaded', handlePageScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <SearchProvider defaultSettings={defaultSettings}>
      <SearchProviderProxy
        settings={providerSettings}
        searchType='search'
      >
        <SearchResultsWithProvider
          query={query}
          setQuery={setQuery}
          setLoading={setLoading}
          settings={settings}
          toggleSidebar={toggleSidebar}
          setSidebarToggle={setSidebarToggle}
          filterButtonSettings={filterButtonSettings}
          sortDropdownSettings={sortDropdownSettings}
          productTileSettings={productTileSettings}
          columns={columns}
          id={id}
          toolbarLayout={toolbar_layout || 'default'}
          productCountPosition={product_count_position || 'center'}
          sidebarPosition={sidebar_position || 'left'}
          showFilterIcon={show_filter_icon !== false}
          showSortLabel={show_sort_label || false}
          filterSortDirection={filter_sort_direction || 'normal'}
          metaobject_filters={metaobject_filters}
          disable_filtering={disable_filtering}
          isSearching={isSearching}
          searchError={searchError}
          searchInputRef={searchInputRef}
        />
      </SearchProviderProxy>
    </SearchProvider>
  );
};

// Internal component that uses the search provider context
function SearchResultsWithProvider({
  query,
  setQuery,
  setLoading,
  settings,
  toggleSidebar,
  setSidebarToggle,
  filterButtonSettings,
  sortDropdownSettings,
  productTileSettings,
  columns,
  id,
  toolbarLayout = 'default',
  productCountPosition = 'center',
  sidebarPosition = 'left',
  showFilterIcon = true,
  showSortLabel = false,
  filterSortDirection = 'normal',
  metaobject_filters,
  disable_filtering,
  isSearching,
  searchError,
  searchInputRef
}) {
  const { popular_searches_title = 'Popular Searches', popular_searches } = settings.section || {};
  const [hasTriggeredInitialSearch, setHasTriggeredInitialSearch] = useState(false);

  const { isMobile, isTablet, isDesktop, isUltrawide } = useResponsiveBreakpoints();

  const search = useSearch();
  // NOTE: Pagination UI and navigation must always use currentPage from SearchContext.
  // If you use any local page state, remove it and rely only on context.currentPage.
  const {
    products,
    filters,
    loading,
    initialLoadComplete,
    totalResults,
    isLastPage,
    currentPage,
    loadMoreProducts,
    activeFilters,
    applyFilters,
    sortOption
  } = useSearchContext();

  // Handle search input changes with proper filter clearing
  const handleSearch = useCallback(
    (searchQuery: string) => {
      setQuery(searchQuery);

      // Clear all filters when search query changes (like in CollectionToolbar)
      applyFilters({}, 'search', false);

      // Update URL with new search query and reset filters/pagination
      if (searchQuery.trim()) {
        // Keep only the search query, remove all filters and pagination
        const newUrl = new URL(window.location.origin + window.location.pathname);
        newUrl.searchParams.set('q', searchQuery);
        window.history.pushState({}, '', newUrl);
      }

      // Fire search request to provider (filters are already cleared)
      const searchRequestEvent = new CustomEvent('searchrequest', {
        detail: {
          type: 'search',
          query: searchQuery,
          filters_object: {}, // Empty filters
          productsPerPage: settings.section?.products_per_page,
          page: 1, // Reset to page 1
          paginationType: 'pages',
          sortOption: settings.section?.default_sort || 'RELEVANCE'
        }
      });

      window.dispatchEvent(searchRequestEvent);
    },
    [setQuery, applyFilters, settings.section?.products_per_page, settings.section?.default_sort]
  );

  // Trigger initial search if there's a query from URL
  useEffect(() => {
    if (query && query.trim() && !hasTriggeredInitialSearch) {
      setHasTriggeredInitialSearch(true);
      // Ensure initial search sets page to 1 in context
      applyFilters({}, 'search', false);
      const searchRequestEvent = new CustomEvent('searchrequest', {
        detail: {
          type: 'search',
          query: query,
          filters_object: {},
          productsPerPage: settings.section?.products_per_page || 24,
          page: 1,
          paginationType: 'pages',
          sortOption: settings.section?.default_sort || 'RELEVANCE'
        }
      });
      window.dispatchEvent(searchRequestEvent);
    }
  }, [
    query,
    hasTriggeredInitialSearch,
    settings.section?.products_per_page,
    settings.section?.default_sort,
    applyFilters
  ]);

  // Product tile features
  const enableWishlist =
    productTileSettings?.product_tile_wishlist_show || settings.section?.product_tile_wishlist_show || true;
  const showPrice =
    productTileSettings?.product_tile_price_show !== false && settings.section?.product_tile_price_show !== false;
  const showQuickAdd =
    productTileSettings?.product_tile_quickadd_show || settings.section?.product_tile_quickadd_show || true;
  const showTags = productTileSettings?.product_tile_tag_show || settings.section?.product_tile_tag_show || true;

  // Memoize stable settings for product rendering (exclude filter/sort related settings)
  const stableProductSettings = useMemo(
    () => ({
      // Only include settings that affect product tile rendering, not filtering/sorting
      product_tile_settings: settings.product_tile_settings,
      mobile_breakpoint: settings.mobile_breakpoint,
      section: settings.section,
      // Add any other settings that ProductTile actually needs
      globals: settings.globals
    }),
    [settings.product_tile_settings, settings.mobile_breakpoint, settings.section, settings.globals]
  );

  // Memoize product tile features to prevent recalculation
  const productTileFeatures = useMemo(
    () => ({
      enableWishlist,
      showPrice,
      showQuickAdd,
      showTags
    }),
    [enableWishlist, showPrice, showQuickAdd, showTags]
  );

  // Pagination logic
  const productsPerPage = settings?.section?.products_per_page || 24;
  const paginationType = settings?.section?.pagination_type || 'pages';
  const totalPages = Math.ceil(totalResults / productsPerPage);
  console.log('totalResults', totalResults, 'totalPages', totalPages, 'productsPerPage', productsPerPage, 'settings?.section?.products_per_page', settings?.section?.products_per_page);
  const isActuallyLastPage = totalResults <= (products?.length || 0) || (products?.length || 0) === 0;

  // Handler for page navigation
  const { changePage } = useSearchContext();
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages && !loading) {
      // Update URL with new page parameter
      const url = new URL(window.location.href);
      url.searchParams.set('page', page);
      window.history.pushState({}, '', url);

      // Only trigger page change via context/provider
      changePage(page, 'search');

      // Scroll to top of results
      const productGrid = document.querySelector('.search-results-container');
      if (productGrid) {
        // Get toolbar height dynamically for scroll margin
          const toolbar = document.querySelector('.collection__toolbar');
          const toolbarHeight = toolbar ? (toolbar as HTMLElement).offsetHeight : 100;

        // Add scroll margin top based on toolbar height
        (productGrid as HTMLElement).style.scrollMarginTop = `${toolbarHeight}px`;
        productGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };

  // Memoize the actual product tiles (without container or loading states)
  const productTiles = useMemo(() => {
    return products.map((product) => (
      <div
        key={`product-${product.id}-${query}-${JSON.stringify(activeFilters)}-${sortOption}`}
        className='search-tile fade-in'
      >
        <ProductTile
          settings={stableProductSettings}
          product={product}
          siblings={undefined}
          globals={{}}
          enableWishlist={productTileFeatures.enableWishlist}
          showPrice={productTileFeatures.showPrice}
          showQuickAdd={productTileFeatures.showQuickAdd}
          showTags={productTileFeatures.showTags}
        />
      </div>
    ));
  }, [
    products,
    productTileFeatures.enableWishlist,
    productTileFeatures.showPrice,
    productTileFeatures.showQuickAdd,
    productTileFeatures.showTags,
    stableProductSettings.product_tile_settings,
    stableProductSettings.mobile_breakpoint,
    stableProductSettings.section,
    stableProductSettings.globals
    // Only depend on actual product data and stable settings - not loading/UI states
  ]);

  // Separate loading and empty state rendering outside of memoized grid
  const loadingAndEmptyStates = (
    <>
      {/* {loading && products.length === 0 && (
        <div className='col-span-full flex justify-center items-center py-8'>
          <LoadingSpinner id="spinner-search" />
          {paginationType === 'pages' && currentPage > 1 && (
            <p className='ml-4 text-gray-600'>Loading page {currentPage}...</p>
          )}
        </div>
      )} */}

      {!loading && products.length === 0 && query && query.length > 0 && initialLoadComplete && (
        <div className='col-span-full text-center py-8'>
          <p className='text-lg mb-2'>No products found</p>
          <p className='text-gray-500'>
            Unfortunately, nothing matches your search. Please try another search term, or browse by category below.
          </p>
        </div>
      )}

      {/* {loading && products.length > 0 && (
        <div className='col-span-full flex justify-center items-center py-8'>
          <div className='w-8 h-8 border-4 border-gray-300 border-t-primary rounded-full animate-spin'></div>
        </div>
      )} */}
    </>
  );

  // Memoize dynamic grid styles based on responsive breakpoints
  const gridStyles = useMemo(() => {
    let gridColumns = columns.mobile;

    if (isUltrawide && columns.ultrawide) {
      gridColumns = columns.ultrawide;
    } else if (isDesktop && columns.desktop) {
      gridColumns = columns.desktop;
    } else if (isTablet && columns.tablet) {
      gridColumns = columns.tablet;
    }

    let columnGap = columns.gapMobile;
    let rowGap = columns.rowGapMobile;

    if (isUltrawide && columns.gapUltrawide) {
      columnGap = columns.gapUltrawide;
    } else if (isDesktop && columns.gapDesktop) {
      columnGap = columns.gapDesktop;
    } else if (isTablet && columns.gapTablet) {
      columnGap = columns.gapTablet;
    }

    if (isUltrawide && columns.rowGapUltrawide) {
      rowGap = columns.rowGapUltrawide;
    } else if (isDesktop && columns.rowGapDesktop) {
      rowGap = columns.rowGapDesktop;
    } else if (isTablet && columns.rowGapTablet) {
      rowGap = columns.rowGapTablet;
    }

    return {
      display: 'grid',
      gridTemplateColumns: `repeat(${gridColumns}, 1fr)`,
      columnGap: `${columnGap}px`,
      rowGap: `${rowGap}px`,
      gridAutoColumns: 'max-content',
      gridAutoFlow: 'dense'
    };
  }, [isMobile, isTablet, isDesktop, isUltrawide, columns]);

  return (
    <div
      data-search-provider={search.settings?.search_system}
      data-current-page={currentPage || 1}
      className='search-results-container'
    >
      {/* Search Results Header */}
      <div className='search-header px-4 lg:px-6'>
        <div className='mb-6 lg:mb-10'>
          <SearchInput
            ref={searchInputRef}
            placeholder={settings?.section?.placeholder_text || 'Search products...'}
            value={query}
            onSearch={handleSearch}
            isLoading={isSearching}
            maintainFocus={true}
            type='search-results'
          />
        </div>

        <div>
          {products.length === 0 && !loading && initialLoadComplete && (
            <div className='mb-6 lg:mb-10 pb-4 lg:pb-8 border-b border-[#C2C2C2]'>
              <p className={`mb-8 lg:mb-10 ${isMobile ? 'b7--bold' : 'b4--bold'}`}>
                No products found for search term '{query}'
              </p>

              {popular_searches && popular_searches.length > 0 && (
                <div
                  className='mt-4 flex items-center gap-x-4 lg:gap-x-12 overflow-y-hidden overflow-x-auto pb-2 custom-scrollbar whitespace-nowrap'
                  x-data='customScrollbar'
                >
                  <h3 className={`${isMobile ? 'b7--bold' : 'b6--bold'}`}>{popular_searches_title}</h3>
                  <ul className='popular-searches-list flex items-center gap-x-3 lg:gap-x-6'>
                    {popular_searches.map((collection, index) => (
                      <li
                        key={index}
                        className='popular-search-item flex items-center'
                      >
                        <a
                          href={`/collections/${collection.handle}`}
                          className={`max-lg:underline lg:hover:underline ${isMobile ? 'b8--medium' : 'b7--medium'}`}
                        >
                          {collection.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {!disable_filtering && (
        <SearchCollectionSidebar
          filters={filters}
          metaobject_filters={metaobject_filters}
          toggleSidebar={toggleSidebar}
          setSidebarToggle={setSidebarToggle}
          sidebarPosition={sidebarPosition}
          show_filter_dividers={true}
          filter_divider_style={'1px solid #e5e7eb'}
        />
      )}
      <div className='search-results__toolbar flex justify-between mb-2 lg:mb-10 items-center relative z-10'>
        <SearchCollectionToolbar
          settings={settings}
          filters={filters}
          toggleSidebar={toggleSidebar}
          filterButtonSettings={filterButtonSettings}
          sortDropdownSettings={sortDropdownSettings}
          toolbarLayout={toolbarLayout}
          productCountPosition={productCountPosition}
          showFilterIcon={showFilterIcon}
          showSortLabel={showSortLabel}
          totalResults={totalResults || products.length}
          filterSortDirection={filterSortDirection}
          isSearchToolbar={true}
          resultsTextSettings={settings.results_text_settings}
          query={query}
        />
      </div>

      <div
        id='product-grid'
        className={`search product-grid`}
        data-id={id}
        style={gridStyles}
      >
        {/* {loadingAndEmptyStates} */}
        {(products?.length || 0) > 0 && productTiles}
      </div>

      {/* Pagination / Load More */}
      {(products?.length || 0) > 0 && !loading && (
        <div className='search__pagination flex justify-center mt-8'>
          <ProductListPagination
            currentPage={currentPage}
            totalPages={totalPages}
            loading={loading}
            goToPage={goToPage}
            loadMoreProducts={loadMoreProducts}
            paginationType={paginationType}
            isActuallyLastPage={isActuallyLastPage}
            paginationTextClass={settings.pagination_text_settings?.font_class || ''}
            paginationActiveColour={settings.pagination_text_settings?.active_colour || ''}
            paginationInactiveColour={settings.pagination_text_settings?.inactive_colour || ''}
          />
        </div>
      )}

      {/* No results Obviyo product recommendations */}
      {products.length === 0 && !loading && initialLoadComplete && (
        <div className='py-6 lg:py-10'>
          <h2 className={`h5 mb-6 lg:mb-10 px-4 lg:px-6`}>
            {settings.section?.recommendation_title || 'Recommended for you'}
          </h2>
          {/* Render Obviyo recommendations if available */}

          <ObviyoProductRecommendations settings={settings.section} />
        </div>
      )}

      {/* Loading spinner */}
      {initialLoadComplete && (
        <Preloader
          animating={loading}
          showBackground={true}
          showLogo={true}
        />
      )}
    </div>
  );
}

const SearchCollectionSidebar = React.memo((props) => {
  return <CollectionSidebar {...props} />;
});

const SearchCollectionToolbar = React.memo((props) => {
  return (
    <CollectionToolbar
      {...props}
      type='search'
      query={props.query}
    />
  );
});

export default SearchResults;
